import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { pacsAPI, queueAPI } from '../../utils/api';
import PatientLookup from '../../components/shared/PatientLookup';
import { printRadiologyOrderSlip } from '../../utils/printSlip';

const MODALITIES = ['X-Ray', 'Ultrasound', 'CT Scan', 'MRI', 'Mammography', 'Fluoroscopy', 'DEXA Scan', 'Nuclear Medicine'];
const MODALITY_CODE = { 'X-Ray': 'XRAY', 'Ultrasound': 'USG', 'CT Scan': 'CT', 'MRI': 'MRI', 'Mammography': 'MAMMO', 'Fluoroscopy': 'FLUORO', 'DEXA Scan': 'DEXA', 'Nuclear Medicine': 'NUC' };

const BODY_PARTS = {
    'X-Ray': [
        'Chest PA', 'Chest AP', 'Chest Lateral',
        'Skull AP', 'Skull Lateral',
        'Paranasal Sinuses', 'Mastoids', 'Mandible / Jaw', 'Orbit',
        'Cervical Spine AP/Lateral', 'Thoracic Spine', 'Lumbar Spine AP/Lateral',
        'Pelvis', 'Hip — Right', 'Hip — Left',
        'Knee — Right', 'Knee — Left',
        'Ankle — Right', 'Ankle — Left',
        'Foot — Right', 'Foot — Left',
        'Hand — Right', 'Hand — Left',
        'Wrist — Right', 'Wrist — Left',
        'Shoulder — Right', 'Shoulder — Left',
        'Elbow — Right', 'Elbow — Left',
        'Abdomen Erect', 'Abdomen Supine',
        'KUB (Kidney Ureter Bladder)',
    ],
    'Ultrasound': [
        'Abdomen (Full)',
        'Upper Abdomen (Liver, GB, Pancreas, Spleen)',
        'Liver', 'Gallbladder & Bile Ducts',
        'Kidney — Right', 'Kidney — Left', 'Both Kidneys',
        'KUB (Kidney Ureter Bladder)',
        'Pelvis (Female)', 'Pelvis (Male)',
        'Obstetric — 1st Trimester', 'Obstetric — 2nd / 3rd Trimester',
        'Thyroid', 'Neck / Lymph Nodes',
        'Breast — Right', 'Breast — Left', 'Breast — Bilateral',
        'Scrotum / Testis',
        'Soft Tissue', 'Musculoskeletal',
        'Doppler — Carotid', 'Doppler — Venous', 'Doppler — Arterial',
        'Guided Aspiration / Biopsy',
    ],
    'CT Scan': [
        'CT Brain — Plain', 'CT Brain — With Contrast', 'CT Brain — Plain + Contrast',
        'CT Angiography — Brain (CTA)', 'CT Paranasal Sinuses',
        'CT Temporal Bones', 'CT Orbit', 'CT Neck',
        'CT Chest — Plain', 'CT Chest — With Contrast',
        'CT Pulmonary Angiography (CTPA)',
        'CT Abdomen — Plain', 'CT Abdomen — With Contrast',
        'CT Abdomen & Pelvis — Plain', 'CT Abdomen & Pelvis — With Contrast',
        'CT KUB (Kidney Ureter Bladder)',
        'CT Spine — Cervical', 'CT Spine — Thoracic', 'CT Spine — Lumbar',
        'CT Coronary Angiography', 'CT Peripheral Angiography', 'CT Whole Body',
    ],
    'MRI': [
        'MRI Brain — Plain', 'MRI Brain — With Contrast', 'MRI Brain — Plain + Contrast',
        'MRA Brain',
        'MRI Spine — Cervical', 'MRI Spine — Thoracic', 'MRI Spine — Lumbar',
        'MRI Brachial Plexus',
        'MRI Knee — Right', 'MRI Knee — Left',
        'MRI Shoulder — Right', 'MRI Shoulder — Left',
        'MRI Hip — Right', 'MRI Hip — Left',
        'MRI Wrist — Right', 'MRI Wrist — Left',
        'MRI Ankle — Right', 'MRI Ankle — Left',
        'MRI Abdomen', 'MRI Pelvis',
        'MRCP (Biliary / Pancreatic)',
        'MRI Breast — Right', 'MRI Breast — Left', 'MRI Breast — Bilateral',
        'MRI Cardiac', 'MRI Soft Tissue',
    ],
    'Mammography': ['Bilateral', 'Right Only', 'Left Only'],
    'Fluoroscopy': ['Barium Swallow', 'Barium Meal', 'Barium Enema', 'IVU (Intravenous Urography)', 'MCU (Micturating Cystourethrogram)', 'Hysterosalpingography (HSG)'],
    'DEXA Scan': ['Whole Body', 'Lumbar Spine', 'Hip — Right', 'Hip — Left', 'Bilateral Hips'],
    'Nuclear Medicine': ['Bone Scan', 'Thyroid Scan', 'Renal Scan', 'Cardiac Stress Test', 'Hepatobiliary Scan'],
};

const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' };

export default function RadiologyRegistration() {
    const [patient, setPatient] = useState(null);
    const [form, setForm] = useState({ modality: '', body_part: '', clinical_info: '', referred_by: '', source: 'Walking', priority: 'Routine', is_contrast: false });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(null);
    const [lastOrder, setLastOrder] = useState(null);
    const patientRef = useRef(null);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const createOrder = async (confirmedPatient) => {
        if (!form.modality || !form.body_part) return toast.error('Please select modality and body part first');
        setLoading(true);
        try {
            const modalityCode = MODALITY_CODE[form.modality] || form.modality;
            const res = await pacsAPI.createOrder({
                patient_id: confirmedPatient.id,
                modality: modalityCode,
                body_part: form.body_part,
                clinical_indication: form.clinical_info,
                referred_by: form.referred_by,
                source: form.source,
                priority: form.priority,
                is_contrast: form.is_contrast,
            });
            toast.success(`Radiology order created: ${res.data.order_number}`);

            const testList = [{ test_name: form.body_part, modality: form.modality, body_part: '' }];
            const orderDetails = { priority: form.priority, source: form.source, clinical_info: form.clinical_info };
            let tokenNumber = null;
            try {
                const tRes = await queueAPI.issueToken({ department: 'RAD', patient_name: confirmedPatient.full_name, patient_id: confirmedPatient.id });
                tokenNumber = tRes.data.token.token_number;
                toast.info(`Queue Token: ${tokenNumber}`);
            } catch {}
            printRadiologyOrderSlip(confirmedPatient, res.data.order_number, testList, orderDetails, tokenNumber);
            setLastOrder({ orderNumber: res.data.order_number, tests: testList, details: orderDetails });
            setDone({ patient: confirmedPatient, orderNumber: res.data.order_number, ...form });
            setForm({ modality: '', body_part: '', clinical_info: '', referred_by: '', source: 'Walking', priority: 'Routine', is_contrast: false });
            setPatient(null);
            setTimeout(() => { setDone(null); patientRef.current?.reset?.(); }, 100);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to create order');
        } finally { setLoading(false); }
    };

    return (
        <div style={{ maxWidth: 700 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <span style={{ fontSize: 32 }}>🩻</span>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: '#4c1d95', margin: 0 }}>Radiology Registration</h2>
                    <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>PACS / Imaging Department</p>
                </div>
            </div>

            {done && (
                <div style={{ background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: 10, padding: 16, marginBottom: 20 }}>
                    <p style={{ color: '#4c1d95', fontWeight: 700, margin: 0 }}>✅ Radiology Order Created!</p>
                    <p style={{ color: '#4c1d95', fontSize: 14, margin: '4px 0 0' }}>
                        Order: <strong>{done.orderNumber}</strong> | {done.patient.full_name} ({done.patient.mrn}) | {done.modality} — {done.body_part}
                    </p>
                </div>
            )}

            {/* 1 - Patient Details */}
            <PatientLookup ref={patientRef} onPatientReady={setPatient} department="Radiology" hideButton onAfterRegister={createOrder}
                onReprint={lastOrder ? (p) => printRadiologyOrderSlip(p, lastOrder.orderNumber, lastOrder.tests, lastOrder.details) : null} />

            {/* 2 - Imaging Details */}
            <div style={{ background: 'white', borderRadius: 10, padding: 24, border: '1px solid #e2e8f0', marginBottom: 16 }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, color: '#4c1d95', marginBottom: 16 }}>Imaging Details</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Source</label>
                        <select style={{ ...inputStyle, background: 'white' }} value={form.source} onChange={e => set('source', e.target.value)}>
                            <option>Walking</option><option>OPD</option><option>ER</option><option>IPD</option><option>ICU</option><option>Other</option>
                        </select>
                    </div>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Priority</label>
                        <select style={{ ...inputStyle, background: 'white' }} value={form.priority} onChange={e => set('priority', e.target.value)}>
                            <option>Routine</option><option>Urgent</option><option>STAT</option>
                        </select>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input type="checkbox" checked={form.is_contrast} onChange={e => set('is_contrast', e.target.checked)} style={{ width: 16, height: 16 }} />
                            <span style={{ fontSize: 13, fontWeight: 600 }}>Contrast Study</span>
                            {form.is_contrast && <span style={{ fontSize: 11, color: '#f97316' }}>Consent needed</span>}
                        </label>
                    </div>
                </div>
                {form.is_contrast && patient?.gender === 'Female' && (
                    <div style={{ background: '#fff7ed', border: '2px solid #fb923c', borderRadius: 8, padding: '10px 14px', marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 20 }}>⚠️</span>
                        <div>
                            <div style={{ fontSize: 13, fontWeight: 800, color: '#c2410c' }}>Contrast + Female Patient — Safety Check Required</div>
                            <div style={{ fontSize: 12, color: '#9a3412', marginTop: 2 }}>Confirm: pregnancy status (LMP / β-hCG), contrast allergy history, and renal function (creatinine) before proceeding.</div>
                        </div>
                    </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Modality *</label>
                        <select style={{ ...inputStyle, background: 'white' }} value={form.modality} onChange={e => { set('modality', e.target.value); set('body_part', ''); }}>
                            <option value="">Select modality</option>
                            {MODALITIES.map(m => <option key={m}>{m}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Study / Body Part *</label>
                        <select style={{ ...inputStyle, background: 'white' }} value={form.body_part} onChange={e => set('body_part', e.target.value)}>
                            <option value="">{form.modality ? 'Select study' : 'Select modality first'}</option>
                            {(BODY_PARTS[form.modality] || []).map(b => <option key={b}>{b}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Referred By</label>
                        <input style={inputStyle} value={form.referred_by} onChange={e => set('referred_by', e.target.value)} placeholder="Doctor / Department" />
                    </div>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Clinical Info</label>
                        <input style={inputStyle} value={form.clinical_info} onChange={e => set('clinical_info', e.target.value)} placeholder="e.g. Rule out pneumonia" />
                    </div>
                </div>
            </div>

            <button type="button" disabled={loading}
                onClick={() => patient ? createOrder(patient) : patientRef.current?.triggerRegister()}
                style={{ padding: '12px 32px', background: loading ? '#9ca3af' : '#059669', color: 'white', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                {loading ? 'Processing...' : patient ? '✅ Create Order' : '✅ Confirm Patient & Continue'}
            </button>
        </div>
    );
}
